import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETensionType } from '@GFTypes/Tension';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';

export class ReelTensionManager {
    private readonly gameStore: GameStore;
    private reelIndexesAfterTension: number[] = [];
    private lastTensionedReelIndex: number = 0;
    private spinWithTension: boolean = false;
    private spinWithFullTension: boolean = false;
    private tensionDurationForEachConsecutiveReel: number[];
    private afterTensionDurationForEachConsecutiveReel: number[];
    private tensionStarted: boolean = false;
    constructor(game: Game) {
        this.gameStore = game.gameStore;

        this.onInit();
    }

    public onInit(): void {
        this.gameStore.on(EGameStoreKeys.SpinParameters, this.setConfiguration, this);
        EventsCenter.on(ALL_EVENTS_LIST.SPIN_STATE, this.reset, this);
        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.updateOnSpinResponse, this);
        EventsCenter.on(EGameEvents.REEL_SPIN_END_$, (data: { eventIds: number[] }) => this.onReelEndSpin(data.eventIds[0]));
    }

    private setConfiguration(): void {
        const { tensionDurationForEachConsecutiveReel, afterTensionDurationForEachConsecutiveReel } = this.gameStore.get(EGameStoreKeys.SpinParameters);
        this.afterTensionDurationForEachConsecutiveReel = afterTensionDurationForEachConsecutiveReel;
        this.tensionDurationForEachConsecutiveReel = tensionDurationForEachConsecutiveReel;
    }

    public updateOnSpinResponse(): void {
        const tension = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps?.at(-1)?.tension;
        if (!tension) return;

        this.spinWithTension = !!tension.reels.some(Boolean);
        this.spinWithFullTension = this.spinWithTension && tension.type === ETensionType.FULL;

        if (!this.spinWithTension) return;

        this.lastTensionedReelIndex = tension.reels.lastIndexOf(true);
    }

    public onReelEndSpin(reelIndex: number): void {
        if (!this.spinWithTension) return;

        const tension = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps?.at(-1)?.tension!;
        const reels = tension.reels;
        const firstReelIndexWithTension = reels.findIndex(Boolean);
        const currentLogicIndex = reelIndex - 1;
        const nextLogicIndex = reelIndex;
        const nextPresentationIndex = reelIndex + 1;
        const isFirstTension = firstReelIndexWithTension === nextLogicIndex;

        if (nextLogicIndex < firstReelIndexWithTension) return;

        if (isFirstTension) {
            this.tensionStarted = true;
            EventsCenter.emit(EGameEvents.TENSION_START_$, { eventIds: [nextPresentationIndex], tensionedReels: reels });
        } else if (reels[nextLogicIndex]) {
            EventsCenter.emit(EGameEvents.TENSION_INTRO_$, { eventIds: [nextPresentationIndex], tensionedReels: reels });
        }

        if (currentLogicIndex >= this.lastTensionedReelIndex && currentLogicIndex < reels.length) {
            EventsCenter.emit(EGameEvents.AFTER_TENSION_START_$, { eventIds: [nextPresentationIndex], tensionedReels: reels });
        }

        if (this.lastTensionedReelIndex === currentLogicIndex) {
            EventsCenter.emit(tension.winning ? EGameEvents.TENSION_WIN_$ : EGameEvents.TENSION_LOSE_$, { eventIds: [reelIndex], tensionedReels: reels });
        }
    }

    public reset(): void {
        this.spinWithTension = false;
        this.spinWithFullTension = false;
        this.tensionStarted = false;
        this.reelIndexesAfterTension = [];
        this.lastTensionedReelIndex = 0;
    }

    public getReelTensionDuration(reelIndex: number): number {
        const tension = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps?.at(-1)?.tension;
        if (!tension) return 0;

        const consecutiveTensionCount = tension.reels.slice(0, reelIndex).filter(Boolean).length;

        return this.tensionDurationForEachConsecutiveReel[consecutiveTensionCount];
    }

    public getAfterReelTensionDuration(reelIndex: number): number {
        return this.afterTensionDurationForEachConsecutiveReel[reelIndex - this.lastTensionedReelIndex - 1];
    }

    public isSpinWithTension(): boolean {
        return this.spinWithTension;
    }

    public isFullTensionPresent(): boolean {
        return this.spinWithFullTension;
    }

    public reelHasTension(reelIndex: number): boolean {
        const tension = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps?.at(0)?.tension;
        if (!tension) return false;
        return tension.reels[reelIndex];
    }

    public isReelAfterLastTensionedReel(reelIndex: number): boolean {
        return this.lastTensionedReelIndex < reelIndex;
    }

    public getLastTensionedReelIndex(): number {
        return this.lastTensionedReelIndex;
    }

    public canQuickStopTension(): boolean {
        const gameType = this.gameStore.get(EGameStoreKeys.GameType);
        const spinParameters = this.gameStore.get(EGameStoreKeys.SpinParameters);
        if (!this.isSpinWithTension() && !this.isFullTensionPresent()) return true;
        if (gameType === 1 && spinParameters.allowQuickStopOnTension) return true;
        if (gameType > 1 && spinParameters.allowQuickStopOnFGTension) return true;
        return false;
    }

    public hasTensionStarted(): boolean {
        return this.tensionStarted;
    }
}
